import { Inject, Injectable } from '@angular/core';
import { mapFetchResultData } from '@x/common/graph';
import {
  ShopActivateMemberInput,
  ShopCreateMemberInput,
  ShopDeactivateMemberInput,
} from '@x/schemas/ecommerce';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ShopActivateMemberGQL,
  ShopCreateMemberGQL,
  ShopDeactivateMemberGQL,
  ShopMemberByIdGQL,
  ShopMembersGQL,
  ShopMembersQueryVariables,
} from './gql/shop-member.gql.generated';
import { AuthorizationHeaderProvider, AUTHORIZATION_HEADER_PROVIDER } from './types/shop-auth';
import { IShopActivateMember, IShopCreateMember, IShopMemberDetail } from './types/shop-member';

@Injectable()
export class ShopMemberService {
  constructor(
    private readonly shopGetMemberById: ShopMemberByIdGQL,
    private shopCreateMember: ShopCreateMemberGQL,
    private shopDeactivateMember: ShopDeactivateMemberGQL,
    private shopActivateMember: ShopActivateMemberGQL,
    private shopMembers: ShopMembersGQL,
    @Inject(AUTHORIZATION_HEADER_PROVIDER)
    private authHeader: AuthorizationHeaderProvider,
  ) {}

  createMember(memberInfo: ShopCreateMemberInput, locale: string): Observable<IShopCreateMember> {
    return this.shopCreateMember
      .mutate({ input: memberInfo, locale }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCreateMember));
  }

  fetchMemberDetail(
    userId: number,
    channelCode: string,
    membershipId: number,
    locale: string,
  ): Observable<IShopMemberDetail | null | undefined> {
    return this.shopGetMemberById
      .fetch({ userId, channelCode, membershipId, locale }, this.authHeader.userAuthHeader)
      .pipe(map((r) => r.data?.shopMember));
  }

  fetchMembers(input: ShopMembersQueryVariables): Observable<IShopMemberDetail[]> {
    return this.shopMembers
      .fetch(input, this.authHeader.userAuthHeader)
      .pipe(map((r) => r.data?.shopMembers));
  }

  deactivateMember(memberInfo: ShopDeactivateMemberInput, locale: string) {
    return this.shopDeactivateMember
      .mutate({ input: memberInfo, locale }, this.authHeader.userAuthHeader)
      .pipe(map((r) => r.data));
  }

  activateMember(
    memberInfo: ShopActivateMemberInput,
    locale: string,
  ): Observable<IShopActivateMember> {
    return this.shopActivateMember
      .mutate({ input: memberInfo, locale }, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopActivateMember));
  }
}
